<template>
  <SeoHead :seo="seo" />
  <MainLayout>
    <SeoBreadcrumbs :seo="seo" />
    <div class="team-show">
      <div class="team-show__toolbar">
        <SeasonSelect
          v-if="seasons.length"
          :model-value="selectedSeasonId"
          :seasons="seasons"
          :multiple="false"
          @change="onSeasonChange"
        />
      </div>

      <header class="ts-header">
        <div class="ts-header-left">
          <PhotoCarousel
            :photos="carouselPhotos"
            :fallback-url="team.image_url"
            :initials="team.short_name || ''"
            :active-season-id="currentSeasonId"
            variant="landscape"
            @open-lightbox="onCarouselOpenLightbox"
            @season-change="onSeasonChange"
          />
          <PhotoLightbox
            :open="lightboxOpen"
            :items="lightboxItems"
            :index="lightboxIndex"
            :variant="lightboxVariant"
            @close="lightboxOpen = false"
            @update:index="lightboxIndex = $event"
          />

          <div v-if="hasAnyContact" class="ts-contacts">
            <a
              v-if="team.instagram_url"
              :href="team.instagram_url"
              target="_blank"
              rel="noopener noreferrer"
              class="ts-contact-link"
              title="Instagram"
            >
              <img src="/images/instagram.png" alt="Instagram" class="ts-contact-icon" />
            </a>
            <a
              v-if="team.contact_phone"
              :href="'tel:' + team.contact_phone"
              class="ts-contact-link"
              :title="team.contact_phone"
            >
              <img src="/images/phone.png" alt="Телефон" class="ts-contact-icon" />
            </a>
            <a
              v-if="team.contact_email"
              :href="'mailto:' + team.contact_email"
              class="ts-contact-link"
              :title="team.contact_email"
            >
              <img src="/images/email.png" alt="Email" class="ts-contact-icon" />
            </a>
            <a
              v-if="team.vk_url"
              :href="team.vk_url"
              target="_blank"
              rel="noopener noreferrer"
              class="ts-contact-link ts-contact-link--text"
              title="VK"
            >VK</a>
            <a
              v-if="team.website"
              :href="team.website"
              target="_blank"
              rel="noopener noreferrer"
              class="ts-contact-link ts-contact-link--text"
              title="Сайт"
            >Сайт</a>
          </div>
        </div>

        <div class="ts-header-info">
          <div class="ts-name-row">
            <button
              v-if="team.logo_url"
              type="button"
              class="sh-logo-slot sh-logo-slot--btn ts-header-logo"
              :title="team.name"
              @click="onHeaderEmblemClick"
            >
              <img
                :src="team.logo_url"
                :alt="team.name"
                class="sh-logo-slot__img"
              />
            </button>
            <span
              v-else
              class="sh-logo-slot ts-header-logo"
              aria-hidden="true"
            />
            <h1 class="ts-name">{{ team.name }}</h1>
          </div>
          <div v-if="team.city" class="ts-city">{{ team.city }}</div>

          <div class="ts-info-grid">
            <div v-if="team.founded_year" class="ts-info-item">
              <span class="ts-info-label">Основан</span>
              <span class="ts-info-value">{{ team.founded_year }}</span>
            </div>
            <div v-if="team.stadium" class="ts-info-item">
              <span class="ts-info-label">Площадка</span>
              <span class="ts-info-value">{{ team.stadium }}</span>
            </div>
          </div>
        </div>
      </header>

      <section v-if="seasonsHistory.length" class="ts-seasons-section">
        <h2 class="ts-section-title sh-section-title">Сезоны</h2>
        <div class="table-scroll sh-table-scroll">
          <div class="ts-seasons-table">
            <div class="ts-seasons-header-row sh-table-header">
              <span class="ts-col ts-col--season">Сезон</span>
              <span class="ts-col ts-col--name">Название</span>
              <span class="ts-col ts-col--places places-track">
                <img src="/images/supercup.png" alt="Суперкубок" class="places-icon" />
                <img src="/images/cup.png" alt="Кубок" class="places-icon" />
                <img src="/images/championship.png" alt="Чемпионат" class="places-icon" />
              </span>
              <span class="ts-col ts-col--kits">Форма</span>
              <span class="ts-col ts-col--photo">Фото</span>
            </div>
            <div
              v-for="(row, rowIndex) in seasonsHistory"
              :key="row.season_id"
              class="ts-seasons-row"
              :class="{
                'sh-row-zebra': rowIndex % 2 === 1,
                'sh-row-active': row.season_id === currentSeasonId,
              }"
              role="button"
              tabindex="0"
              @click="onSeasonChange(row.season_id)"
              @keydown.enter.prevent="onSeasonChange(row.season_id)"
              @keydown.space.prevent="onSeasonChange(row.season_id)"
            >
              <span class="ts-col ts-col--season" data-label="Сезон">
                <span class="season-label">
                  <span class="season-label__full">{{ row.season_name }}</span>
                  <span class="season-label__short">{{ row.season_short || formatSeasonYearShort(row.season_name) }}</span>
                </span>
              </span>
              <span class="ts-col ts-col--name" data-label="Название">
                <span class="ts-season-team">
                  <button
                    v-if="row.logo_url"
                    type="button"
                    class="sh-logo-slot sh-logo-slot--btn"
                    :title="row.name"
                    @click.stop="onEmblemClick(row)"
                  >
                    <img
                      :src="row.logo_url"
                      :alt="row.name"
                      class="sh-logo-slot__img"
                    />
                  </button>
                  <span
                    v-else
                    class="sh-logo-slot"
                    aria-hidden="true"
                  />
                  <span class="ts-season-team-name">{{ row.name }}</span>
                </span>
              </span>
              <span class="ts-col ts-col--places places-track" data-label="Места">
                <div class="places-item"><PlaceBadge :place="row.places?.supercup" /></div>
                <div class="places-item"><PlaceBadge :place="row.places?.cup" /></div>
                <div class="places-item"><PlaceBadge :place="row.places?.championship" /></div>
              </span>
              <span class="ts-col ts-col--kits" data-label="Форма">
                <span class="sh-kits-slot">
                  <span class="sh-kit-cell">
                    <JerseyNumber
                      v-if="row.primary_color"
                      show-kit
                      :primary-color="row.primary_color"
                      size="compact"
                    />
                  </span>
                  <span class="sh-kit-cell">
                    <JerseyNumber
                      v-if="row.secondary_color"
                      show-kit
                      :primary-color="row.secondary_color"
                      size="compact"
                    />
                  </span>
                </span>
              </span>
              <span class="ts-col ts-col--photo" data-label="Фото">
                <button
                  v-if="row.photo_url"
                  type="button"
                  class="sh-photo-thumb"
                  :title="row.season_short || row.season_name"
                  @click.stop="onSeasonPhotoClick(row)"
                  @keydown.enter.stop.prevent="onSeasonPhotoClick(row)"
                  @keydown.space.stop.prevent="onSeasonPhotoClick(row)"
                >
                  <img
                    :src="row.photo_url"
                    :alt="row.season_name || 'Фото'"
                    class="sh-photo-thumb__img"
                    loading="lazy"
                    decoding="async"
                  />
                </button>
              </span>
            </div>
          </div>
        </div>
      </section>

      <div class="ts-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          :class="['ts-tab', { active: activeTab === tab.key }]"
          @click="activeTab = tab.key"
        >
          {{ tab.name }}
        </button>
      </div>

      <div class="ts-tab-content">
        <div v-if="activeTab === 'staff'" class="ts-members">
          <div v-if="staff.length === 0" class="ts-empty">
            Информация о тренерском штабе отсутствует
          </div>
          <div v-else class="ts-members-grid">
            <Link
              v-for="member in staff"
              :key="member.id"
              :href="member.url"
              class="ts-member-card"
            >
              <div class="ts-member-photo">
                <img
                  v-if="member.photo_url"
                  :src="member.photo_url"
                  :alt="member.full_name"
                  class="ts-member-photo-img"
                />
                <div v-else class="ts-member-photo-placeholder">
                  {{ member.full_name.charAt(0) }}
                </div>
              </div>
                <div class="ts-member-info">
                <div class="ts-member-name">
                  <RegisteredBadge :status="member.status" size="sm" />
                  {{ member.full_name }}
                </div>
                <div class="ts-member-position">{{ member.position }}</div>
              </div>
            </Link>
          </div>
        </div>

        <div v-else class="ts-members">
          <div v-if="roster.length === 0" class="ts-empty">
            Состав команды на выбранный сезон не найден
          </div>
          <div v-else class="ts-members-grid">
            <Link
              v-for="player in roster"
              :key="player.id"
              :href="player.url"
              class="ts-member-card"
            >
              <div class="ts-member-photo ts-member-photo--portrait">
                <img
                  v-if="player.photo_url"
                  :src="player.photo_url"
                  :alt="player.full_name"
                  class="ts-member-photo-img"
                />
                <div v-else class="ts-member-photo-placeholder">
                  {{ player.full_name.charAt(0) }}
                </div>
              </div>
              <div class="ts-member-info">
                <div class="ts-member-name">
                  <JerseyNumber
                    v-if="player.number"
                    :number="player.number"
                    :primary-color="team.primary_color"
                    :secondary-color="team.secondary_color"
                    size="compact"
                    class="ts-member-jersey"
                  />
                  <RegisteredBadge :status="player.status" size="sm" />
                  {{ player.full_name }}
                </div>
                <div class="ts-member-position">{{ player.position }}</div>
                <div
                  v-if="player.birth_date || player.height"
                  class="ts-member-meta"
                >
                  <span v-if="player.birth_date">{{ player.birth_date }}</span>
                  <span v-if="player.height"> · {{ player.height }} см</span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  </MainLayout>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import SeoHead from '@/Components/SeoHead.vue';
import SeoBreadcrumbs from '@/Components/SeoBreadcrumbs.vue';
import MainLayout from '@/components/layout/MainLayout.vue';
import SeasonSelect from '@/components/filters/SeasonSelect.vue';
import PlaceBadge from '@/components/player/PlaceBadge.vue';
import PhotoCarousel from '@/components/player/PhotoCarousel.vue';
import PhotoLightbox from '@/Components/ui/PhotoLightbox.vue';
import JerseyNumber from '@/Components/shared/JerseyNumber.vue';
import RegisteredBadge from '@/Components/shared/RegisteredBadge.vue';
import { formatSeasonYearShort } from '@/utils/formatSeason';

const props = defineProps({
  seo: {
    type: Object,
    default: null,
  },
  team: {
    type: Object,
    required: true,
  },
  staff: {
    type: Array,
    required: true,
  },
  roster: {
    type: Array,
    default: () => [],
  },
  seasonsHistory: {
    type: Array,
    default: () => [],
  },
  carouselPhotos: {
    type: Array,
    default: () => [],
  },
  seasons: {
    type: Array,
    required: true,
  },
  currentSeasonId: {
    type: Number,
    default: null,
  },
  currentSeason: {
    type: Object,
    default: null,
  },
});

const activeTab = ref('roster');
const selectedSeasonId = computed(() => props.currentSeasonId);

const lightboxOpen = ref(false);
const lightboxIndex = ref(0);
const lightboxItems = ref([]);
const lightboxVariant = ref('default');

const tabs = [
  { key: 'roster', name: 'Состав' },
  { key: 'staff', name: 'Штаб' },
];

const hasAnyContact = computed(() =>
  Boolean(
    props.team.instagram_url
    || props.team.vk_url
    || props.team.contact_phone
    || props.team.contact_email
    || props.team.website,
  ),
);

const emblemLightboxItems = computed(() =>
  (props.seasonsHistory || [])
    .filter((row) => row?.logo_url)
    .map((row) => ({
      url: row.logo_url,
      caption: [row.season_short || formatSeasonYearShort(row.season_name), row.name]
        .filter(Boolean)
        .join(' · '),
    })),
);

function openLightbox(items, index = 0, variant = 'default') {
  if (!items.length) return;
  lightboxVariant.value = variant;
  lightboxItems.value = items;
  lightboxIndex.value = Math.max(0, Math.min(index, items.length - 1));
  lightboxOpen.value = true;
}

function onCarouselOpenLightbox(payload) {
  const items = (props.carouselPhotos || [])
    .filter((photo) => photo?.url)
    .map((photo) => ({
      url: photo.url,
      caption: photo.caption || photo.season_name || '',
    }));

  if (!items.length && payload?.url) {
    openLightbox([{ url: payload.url, caption: payload?.photo?.caption || '' }], 0, 'default');
    return;
  }

  const url = payload?.url;
  const idx = url
    ? items.findIndex((item) => item.url === url)
    : (payload?.index ?? 0);
  openLightbox(items, idx >= 0 ? idx : 0, 'default');
}

function onSeasonPhotoClick(row) {
  if (!row?.photo_url) return;

  const items = (props.carouselPhotos || [])
    .filter((photo) => photo?.url)
    .map((photo) => ({
      url: photo.url,
      caption: photo.caption || photo.season_name || '',
    }));

  if (!items.length) {
    openLightbox([{
      url: row.photo_url,
      caption: row.season_short || row.season_name || '',
    }], 0, 'default');
    return;
  }

  const idx = items.findIndex((item) => item.url === row.photo_url);
  openLightbox(items, idx >= 0 ? idx : 0, 'default');
}

function onEmblemClick(row) {
  const items = emblemLightboxItems.value;
  const idx = items.findIndex((item) => item.url === row.logo_url);
  openLightbox(items, idx >= 0 ? idx : 0, 'framed');
}

function onHeaderEmblemClick() {
  if (!props.team.logo_url) return;
  const items = emblemLightboxItems.value;
  const idx = items.findIndex((item) => item.url === props.team.logo_url);
  if (idx >= 0) {
    openLightbox(items, idx, 'framed');
    return;
  }
  openLightbox([{
    url: props.team.logo_url,
    caption: props.team.name || '',
  }], 0, 'framed');
}

function onSeasonChange(seasonId) {
  if (seasonId === null || seasonId === undefined || seasonId === '') {
    return;
  }

  if (seasonId === props.currentSeasonId) {
    return;
  }

  router.get(
    props.team.url,
    { season_id: seasonId },
    {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    },
  );
}
</script>
